import { createApiError } from '@/core/error-handling/ApiErrorHandler';
import type {
  EmployeeCountRange,
  OrganizationRecord,
  OrganizationWritePayload,
  PhoneCountryCodeItem,
  ReferenceItem,
} from '../../models';

/**
 * Mock backend for step 1, shaped to
 * mdfiles/organization-setup-step1-apiflow.md.
 *
 * Unlike the other fixtures in this folder, this one holds STATE: create
 * assigns a code and stores the record, update replaces it, and get reads it
 * back. Without that, Save & Next then Back would show stale data and the
 * POST-then-PUT transition could not be exercised at all in mock mode.
 */

/* -- reference data -------------------------------------------------------- */

const COUNTRIES: readonly ReferenceItem[] = [
  { id: 7, name: 'India' },
  { id: 45, name: 'United Kingdom' },
  { id: 12, name: 'United States' },
];

/** Keyed by countryId, matching the /states?countryId= contract. */
const STATES: Readonly<Record<number, readonly ReferenceItem[]>> = {
  7: [
    { id: 9, name: 'Karnataka' },
    { id: 6, name: 'Maharashtra' },
  ],
  45: [{ id: 31, name: 'England' }],
  12: [
    { id: 21, name: 'California' },
    { id: 24, name: 'New York' },
  ],
};

/** Keyed by stateId, which is the city's own parent. */
const CITIES: Readonly<Record<number, readonly ReferenceItem[]>> = {
  6: [
    { id: 6, name: 'Mumbai' },
    { id: 14, name: 'Pune' },
  ],
  9: [{ id: 18, name: 'Bengaluru' }],
  31: [{ id: 40, name: 'London' }],
  21: [{ id: 52, name: 'San Francisco' }],
  24: [{ id: 55, name: 'New York City' }],
};

const EMPLOYEE_COUNT_RANGES: readonly EmployeeCountRange[] = [
  { code: 'FROM_0_TO_50', label: '0-50' },
  { code: 'FROM_51_TO_100', label: '51-100' },
  { code: 'FROM_101_TO_500', label: '101-500' },
  { code: 'FROM_501_TO_1000', label: '501-1000' },
  { code: 'MORE_THAN_1000', label: 'More than 1000' },
];

export function mockCountries(): ReferenceItem[] {
  return [...COUNTRIES];
}

export function mockStates(countryId: number): ReferenceItem[] {
  return [...(STATES[countryId] ?? [])];
}

export function mockCities(stateId: number): ReferenceItem[] {
  return [...(CITIES[stateId] ?? [])];
}

export function mockEmployeeCountRanges(): EmployeeCountRange[] {
  return [...EMPLOYEE_COUNT_RANGES];
}

/**
 * The six dial codes the platform seeds, in the shape
 * GET /phone-country-codes returns them.
 */
const PHONE_COUNTRY_CODES: PhoneCountryCodeItem[] = [
  { id: 7, name: 'India', code: '+91', isoCode: 'IN' },
  { id: 8, name: 'United Arab Emirates', code: '+971', isoCode: 'AE' },
  { id: 9, name: 'United States', code: '+1', isoCode: 'US' },
  { id: 10, name: 'United Kingdom', code: '+44', isoCode: 'GB' },
  { id: 11, name: 'Singapore', code: '+65', isoCode: 'SG' },
  { id: 12, name: 'Australia', code: '+61', isoCode: 'AU' },
];

export function mockPhoneCountryCodes(): PhoneCountryCodeItem[] {
  return [...PHONE_COUNTRY_CODES];
}

/* -- organization store ---------------------------------------------------- */

/**
 * Section 6: Pending on create, Active once step 6 completes.
 *
 * These are the platform's own ids (`os_status_master`: 10 Pending, 11 Active).
 * Mock mode has to agree with the server about what a freshly created
 * organization looks like, or a screen tested against mocks shows a status the
 * real API never returns.
 */
const STATUS_PENDING = { id: 10, name: 'Pending' };

const SEEDED_ORGANIZATION: OrganizationRecord = {
  id: 42,
  organizationCode: 'ORG-014',
  organizationName: 'AIT Global',
  orgStartDate: '2020-04-01T00:00:00',
  countryId: 7,
  phoneCodeId: 7,
  /* One dial code per number, since V20261002090000. */
  secondaryPhoneCodeId: 7,
  countryName: 'India',
  stateId: 6,
  stateName: 'Maharashtra',
  cityId: 14,
  cityName: 'Pune',
  totalEmployees: '501-1000',
  gstReferenceId: '27AABCU9603R1ZM',
  panNumber: 'AABCU9603R',
  registrationNumber: 'U72200MH2009PTC190890',
  userLicense: 250,
  organizationDomains: ['aitglobal.com', 'example.test'],
  address: 'Level 4, Tower B, Cyber City',
  description: 'Global technology services',
  // A 1x1 PNG -- enough for the resume path to carry a real data URI.
  orgImage:
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8DwHwAFAAH/q842iQAAAABJRU5ErkJggg==',
  primaryContactName: 'Ashok Kumar',
  phoneNumber: '9876543210',
  secondaryPhoneNumber: '9876543211',
  orgHead: 'Ashok Kumar',
  secondaryEmail: '',
  email: 'ashok.kumar@aitglobal.com',
  statusId: STATUS_PENDING.id,
  statusName: STATUS_PENDING.name,
  createdBy: 1,
  createdDate: '2026-09-09T10:15:30',
  modifiedBy: 1,
  modifiedDate: '2026-09-09T10:15:30',
};

/**
 * Set VITE_MOCK_ORG_STATE=new to start with no organization, which exercises
 * the create branch (POST + the /users/me refresh) instead of the update one.
 * Mirrors VITE_MOCK_MENU_STATE in the sidebar service.
 */
function startsEmpty(): boolean {
  return import.meta.env.VITE_MOCK_ORG_STATE === 'new';
}

let organizations = new Map<string, OrganizationRecord>();
let nextId = SEEDED_ORGANIZATION.id;
let nextCodeSequence = 14;

function seed(): void {
  organizations = new Map();
  nextId = SEEDED_ORGANIZATION.id;
  nextCodeSequence = 14;

  if (!startsEmpty()) {
    organizations.set(SEEDED_ORGANIZATION.organizationCode, { ...SEEDED_ORGANIZATION });
  }
}

seed();

/**
 * What a mock GET /users/me reports as `organizationCode`.
 *
 * Live, not fixed: before any create it is null (or the seeded code), and
 * after a create it is the code that create assigned -- which is exactly the
 * transition section 5.1's post-create refresh exists to observe.
 */
export function mockCurrentOrganizationCode(): string | null {
  const codes = [...organizations.keys()];
  return codes.length > 0 ? (codes[codes.length - 1] ?? null) : null;
}

function formatCode(sequence: number): string {
  return `ORG-${String(sequence).padStart(3, '0')}`;
}

/**
 * Section 4.5: peeks at the sequence without reserving it, so it returns what
 * the NEXT create would take rather than anything durable.
 */
export function mockNextOrganizationCode(): string {
  return formatCode(nextCodeSequence + (organizations.size > 0 ? 1 : 0));
}

export function mockMarkOrganizationActive(organizationCode: string): void {
  const found = organizations.get(organizationCode);
  if (found) {
    found.statusId = 11;
    found.statusName = 'Active';
  }
}

export function mockGetOrganization(organizationCode: string): OrganizationRecord {
  const found = organizations.get(organizationCode);
  if (!found) {
    throw createApiError({
      message: `Organization ${organizationCode} not found`,
      code: 'NOT_FOUND_ERROR',
      status: 404,
    });
  }
  return { ...found };
}

/** Resolves an employee-count `code` to its label, as the server does. */
function resolveEmployeeLabel(value: string): string {
  const trimmed = value.trim();
  const match = EMPLOYEE_COUNT_RANGES.find(
    (range) =>
      range.code.toLowerCase() === trimmed.toLowerCase() ||
      range.label.toLowerCase() === trimmed.toLowerCase(),
  );
  return match?.label ?? trimmed;
}

function nameOf(list: readonly ReferenceItem[], id: number | null): string | null {
  return list.find((item) => item.id === id)?.name ?? null;
}

/** Builds the response record, resolving every FK id to its name. */
function toRecord(
  base: { id: number; organizationCode: string; createdDate: string },
  payload: OrganizationWritePayload,
  statusId: number,
  statusName: string,
): OrganizationRecord {
  const now = new Date().toISOString().replace(/\.\d{3}Z$/, '');

  return {
    id: base.id,
    organizationCode: base.organizationCode,
    organizationName: payload.organizationName,
    orgStartDate: payload.orgStartDate,
    countryId: payload.countryId,
    countryName: nameOf(COUNTRIES, payload.countryId),
    stateId: payload.stateId,
    stateName: nameOf(STATES[payload.countryId ?? -1] ?? [], payload.stateId),
    cityId: payload.cityId,
    cityName: nameOf(CITIES[payload.stateId ?? -1] ?? [], payload.cityId),
    totalEmployees: resolveEmployeeLabel(payload.totalEmployees),
    gstReferenceId: payload.gstReferenceId,
    panNumber: payload.panNumber,
    registrationNumber: payload.registrationNumber,
    userLicense: payload.userLicense,
    organizationDomains: payload.organizationDomains,
    address: payload.address,
    description: payload.description,
    orgImage: payload.orgImage,
    orgImageName: payload.orgImageName,
    primaryContactName: payload.primaryContactName,
    phoneCodeId: payload.phoneCodeId,
    secondaryPhoneCodeId: payload.secondaryPhoneCodeId,
    phoneNumber: payload.phoneNumber,
    secondaryPhoneNumber: payload.secondaryPhoneNumber,
    orgHead: payload.orgHead,
    secondaryEmail: payload.secondaryEmail,
    email: payload.email,
    statusId,
    statusName,
    createdBy: 1,
    createdDate: base.createdDate,
    modifiedBy: 1,
    modifiedDate: now,
  };
}

export function mockCreateOrganization(
  payload: OrganizationWritePayload,
): OrganizationRecord {
  nextCodeSequence += 1;
  nextId += 1;

  const created = toRecord(
    {
      id: nextId,
      organizationCode: formatCode(nextCodeSequence),
      createdDate: new Date().toISOString().replace(/\.\d{3}Z$/, ''),
    },
    payload,
    STATUS_PENDING.id,
    STATUS_PENDING.name,
  );

  organizations.set(created.organizationCode, created);
  return { ...created };
}

export function mockUpdateOrganization(
  organizationCode: string,
  payload: OrganizationWritePayload,
): OrganizationRecord {
  const existing = organizations.get(organizationCode);
  if (!existing) {
    throw createApiError({
      message: `Organization ${organizationCode} not found`,
      code: 'NOT_FOUND_ERROR',
      status: 404,
    });
  }

  const updated = toRecord(
    {
      id: existing.id,
      organizationCode,
      createdDate: existing.createdDate ?? new Date().toISOString(),
    },
    payload,
    existing.statusId ?? STATUS_PENDING.id,
    existing.statusName ?? STATUS_PENDING.name,
  );

  organizations.set(organizationCode, updated);
  return { ...updated };
}

/** Test-only reset, so wizard state cannot leak between cases. */
export function resetMockOrganizations(): void {
  seed();
}
